import React, { useContext, useEffect, useState } from "react";
import ConnectionCard from "./ConnectionCard";
import { Box, Text, ThemeContext } from "grommet";
import FontAwesomeIcon from "../../FontAwesomeIcon";
import { faPlus, faSync } from "@fortawesome/pro-light-svg-icons";
import {ICON_WIDTH_LARGE, LOGO_SIZE_MEDIUM_SMALL} from "../../../constants/Dimensions";
import { handleErrors } from "../../../utils/errors";
import { NotificationsContext } from "../../NotificationsProvider";
import useSWR, { useSWRConfig } from "swr";
import { pollNetsuiteSyncStatus, syncNetSuite } from "../../../utils/connections";
import Script from "next/script";
import { formatDate, formatTime, formatLastSynced } from "../../../utils/dates";
import NextAnchor from "../../NextAnchor";
import Button from "../../Button";
import { NETSUITE_CONNECTION_STATUS } from "../../../constants/Connections";
import withPlanGating from "../../hoc/withPlanGating";

const NetSuiteConnectionComponent = ({ planGating }) => {
    const [isSyncing, setIsSyncing] = useState();
    const [connectionStatus, setConnectionStatus] = useState();
    const [checkedOnInit, setCheckedOnInit] = useState(false); // a flag to keep track of whether or not we initialize polling on page load, if netsuite has sync in progress
    const notificationCtx = useContext(NotificationsContext);
    const { mutate } = useSWRConfig();

    const fetcher = (url) => fetch(url)
        .then(handleErrors)
        .catch(notificationCtx.showNotification);

    const {data: syncData} = useSWR("/api/settings/netsuite", fetcher, {
        revalidateOnFocus: false,
    });

    useEffect(() => {
        if (!syncData) return;

        onSyncDataChanged(syncData);

        // Check netsuite connection on page load, see if we need to start polling or kick off sync
        if (!checkedOnInit) {
            const status = syncData?.connection_status;

            setCheckedOnInit(true);
            // if not connected, don't poll or do anything
            if (!status) return; 

            // if NetSuite is concected, but there's no last sync, then we want to kick off sync
            if (status === NETSUITE_CONNECTION_STATUS.CONNECTED && !syncData?.last_synced) {
                onSync();
                return;
            }
            // if Netsuite is syncing or connecting, we want to keep polling to check status
            if (isSyncing) {
                let poll = setInterval(
                    () => pollNetsuiteSyncStatus(notificationCtx.showNotification, onSyncDataChanged),
                    5000
                );
                return () => clearInterval(poll);
            }
        }
    }, [syncData]);

    const onSyncDataChanged = (data) => {
        if (!data) return;
        const {
            sync_in_progress: syncInProgress,
            connection_status: status,
            last_synced: lastSyncedAt
        } = data;
        setConnectionStatus(status);
        setIsSyncing(syncInProgress || 
            status === NETSUITE_CONNECTION_STATUS.LINKED ||  // if account is linked but not connected, we'll keep polling
            (status === NETSUITE_CONNECTION_STATUS.CONNECTED && !lastSyncedAt)); // if account is connected and has no last sync, we will subsequently kick off a sync
    }

    const onSync = () => {
        setIsSyncing(true);
        syncNetSuite(
            notificationCtx.showNotification,
            () => {},
            data => {
                onSyncDataChanged(data);
                mutate("/api/settings/netsuite");
            }
        )
    };

    const onLinkNetSuite = () => {
        if (typeof Rutter === "undefined") return; // not initialized yet
        var rutterInstance = Rutter.create({
            publicKey: process.env.RUTTER_PUBLIC_KEY,
            onSuccess: (publicToken) => {
                fetch("/api/settings/netsuite", { // create netsuite connection
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        public_token: publicToken
                    })
                })
                    .then(handleErrors)
                    .then(() => {
                        mutate("/api/settings/netsuite");
                        // start polling to check if connection is set up
                        let poll = setInterval(
                            () =>
                                // poll status to see if the store is linked
                                pollNetsuiteSyncStatus(notificationCtx.showNotification, (data) => {
                                    onSyncDataChanged(data);
                                    if (data?.connection_status === NETSUITE_CONNECTION_STATUS.CONNECTED) { // if netsuite account is linked, initiate sync
                                        clearInterval(poll);
                                        onSync();
                                    }
                                }),
                            5000
                        );
                    });
            }
        })
        rutterInstance.open();
    };

    const renderSyncNetsuiteButton = () => {
        return (
            <Button
                event={{section: 'netsuite_connection_component', element: 'sync_netsuite'}}
                icon={
                    <Box animation={isSyncing ? { type: "rotateRight", duration: 1350 } : undefined}>
                        <FontAwesomeIcon SelectedIcon={faSync} size="xsmall" />
                    </Box>
                }
                // Disable the sync state
                disabled={isSyncing}
                label={<Text>{isSyncing ? "Sync in progress" : "Sync NetSuite"}</Text>}
                onClick={onSync}
                primary
                ctaColor={isSyncing ? "neutral-5": "status-ok"}
            />
        );
    };

    const renderNetsuiteOauth = () => {
        const originalButton = (
            <Button
                event={{section: 'netsuite_connection_component', element: 'connect_account'}}
                type="submit"
                label={"Connect account"}
                icon={(
                    <FontAwesomeIcon SelectedIcon={faPlus} size="xsmall" />
                )}
                disabled
                ctaColor={"neutral-6"}
                onClick={onLinkNetSuite}
            />
        );

        // If plan gating is active and user doesn't have required plan, render disabled button
        if (planGating && !planGating.hasRequiredPlan) {
            return planGating.buttonRenderer(originalButton);
        }

        //return originalButton; #disable until we replace rutter.
        return (
            <Button
                event={{section: 'netsuite_connection_component', element: 'unavailable'}}
                type="submit"
                primary
                label={"Unavailable"}
                ctaColor={"neutral-6"}
                disabled
            />
        )

    };

    const getDetailsText = () => {
        if (syncData && connectionStatus === NETSUITE_CONNECTION_STATUS.CONNECTED && syncData?.last_synced) {
            return [formatLastSynced(syncData?.last_synced)];
        }

        // If plan gating is active and user doesn't have required plan, show teaser content
        if (planGating && !planGating.hasRequiredPlan) {
            return planGating.teaserContent;
        }

        // If user has required plan, show support message
        return [
            "Please contact Streamlined support to enable this connector."
        ];
    }

    return (
        <ThemeContext.Consumer>
            {(theme) => (
                <ConnectionCard
                    connectionName="Oracle NetSuite"
                    data={syncData}
                    logoSrc={`/brand_logos/netsuite_symbol${theme.dark ? "_white" : ""}.svg`}
                    isLoaded={syncData}
                    details={getDetailsText()}
                    cta={connectionStatus === NETSUITE_CONNECTION_STATUS.CONNECTED || connectionStatus === NETSUITE_CONNECTION_STATUS.LINKED ?
                        renderSyncNetsuiteButton() :
                        renderNetsuiteOauth()}
                >
                    <Script src="https://unpkg.com/@rutter/rutter-link@latest" type="text/javascript" />
                </ConnectionCard>
            )}
        </ThemeContext.Consumer>
    );
};

// Wrap the component with plan gating HOC
const PlanGatedNetSuiteConnectionComponent = withPlanGating(NetSuiteConnectionComponent, {
    requiredPlans: ['enterprise'],
    featureName: 'NetSuite integration',
    dataSource: 'session'
});

export default PlanGatedNetSuiteConnectionComponent;
